import { Control, Controller, FieldErrors } from "react-hook-form";

import {
  Grid,
  Input,
  Paragraph,
  Select,
  XStack,
  YStack,
  getGridLayout,
} from "@/components";
import { US_STATES } from "@/settings/constants";
import type { UserProfileSchemaType } from "../tabs/profile";

export interface LocationInformationProps {
  control: Control<UserProfileSchemaType>;
  errors: FieldErrors<UserProfileSchemaType>;
  zipcodeError?: string;
}

export function LocationInformation({
  control,
  errors,
  zipcodeError,
}: LocationInformationProps) {
  return (
    <Grid>
      <YStack
        {...getGridLayout(6)}
        $lg={{
          width: "50%",
          paddingRight: "$100",
        }}
        gap="$100"
        marginBottom="$100"
      >
        <Controller
          control={control}
          name="residingState"
          render={({ field: { onChange, ref, value } }) => (
            <Select
              ref={ref}
              label="Current state"
              items={US_STATES}
              value={value}
              onValueChange={onChange}
              placeholder="Select state"
            />
          )}
        />
        <Paragraph size="$xs" color="$textSecondary">
          We'll connect you with providers licensed in this state.
        </Paragraph>
      </YStack>
      <Controller
        control={control}
        name="streetAddress"
        render={({ field: { onChange, onBlur, ref, value } }) => (
          <Input
            ref={ref}
            label="Home address"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            placeholder="Home address"
            errorText={errors.streetAddress?.message}
            rootProps={{
              ...getGridLayout(6),
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="streetAddress2"
        render={({ field: { onChange, onBlur, ref, value } }) => (
          <Input
            ref={ref}
            label="Apartment, suite, etc."
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            placeholder="Apartment, suite, etc."
            rootProps={{
              ...getGridLayout(6),
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="city"
        render={({ field: { onChange, onBlur, ref, value } }) => (
          <Input
            ref={ref}
            label="City"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            placeholder="City"
            errorText={errors.city?.message}
            rootProps={{
              ...getGridLayout(6),
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="state"
        render={({ field: { onChange, ref, value } }) => (
          <Select
            ref={ref}
            label="State"
            items={US_STATES}
            value={value}
            onValueChange={onChange}
            placeholder="Select state"
            errorText={errors.state?.message}
            {...getGridLayout(6)}
          />
        )}
      />
      <XStack
        {...getGridLayout(6)}
        $lg={{
          ...getGridLayout(12),
        }}
      >
        <Controller
          control={control}
          name="zipCode"
          render={({ field: { onChange, onBlur, ref, value } }) => (
            <Input.Controlled
              ref={ref}
              label="Zip code"
              value={value ?? ""}
              onChangeText={onChange}
              onBlur={onBlur}
              inputMode="numeric"
              maxLength={5}
              placeholder="Zip code"
              errorText={zipcodeError || errors.zipCode?.message}
              rootProps={{
                width: "100%",
                $lg: {
                  width: "50%",
                },
              }}
            />
          )}
        />
      </XStack>
    </Grid>
  );
}
